import { classificationSchema } from "@/lib/esco";
import { z } from "zod";

import { MissingPowerScore, PowerScoreLens } from "@/lib/score-source";
import { MATCH_MODES } from "./match-mode";

const Operator = z.enum(["GT", "GE", "LT", "LE", "EQ", "NE"]);
export type Operator = z.infer<typeof Operator>;

const CompositionOperator = z.enum(["MIN", "MAX", "EXACT", "ANY", "ALL"]);
export type CompositionOperator = z.infer<typeof CompositionOperator>;

export const Composition = z.object({
  operator: CompositionOperator,
  // A peer client (discovery_app, KMP/Swift) can serialise an absent count as
  // the JSON *string* "null" instead of real null. Coerce it back so the row
  // still validates; without this the whole condition fails to parse and
  // parseDesign would drop it -> deleted on the next autosave (2026-07-17
  // Expertise/Industry data-loss incident). On save this side re-emits proper
  // JSON null, healing the row end-to-end.
  value: z.preprocess((v) => (v === "null" ? null : v), z.number().int().nullable().optional()),
});
export type Composition = z.infer<typeof Composition>;

const AiMode = z.enum(["NO_AI", "CONTEXT_AI", "FULL_AI"]);
export type AiMode = z.infer<typeof AiMode>;

// On read, coerce a missing/null aiMode to NO_AI. Genesis records permit
// `aiMode = null` (the Java record has no @NonNull) and at least one JVM-side
// caller – MatchServiceDesignTest – writes that today; legacy data persisted
// before the global "KI-Unterstützung" toggle convention
// can also carry it. Without this coercion the discriminated-union variant
// would fail to parse and parseDesign() would silently drop the whole row.
// NO_AI is the conservative default – it disables per-candidate model-gateway
// calls during solve, which is what a user looking at an unset toggle expects.
const AiModeOnRead = z.preprocess((v) => v == null ? "NO_AI" : v, AiMode);
// The same coercion for the COMPANY arm, defaulting the other way: a draft written before concept
// v17 carries no aiMode, and before v17 the company AI was on without exception. See the
// CompanyExpertiseCondition shape below.
const CompanyAiModeOnRead = z.preprocess((v) => (v == null ? "FULL_AI" : v), AiMode);

export const LanguageLevel = z.enum(["A1", "A2", "B1", "B2", "C1", "C2", "NATIVE"]);
export type LanguageLevel = z.infer<typeof LanguageLevel>;

export const RequirementMode = z.enum(["COPILOT_ONLY", "HYBRID", "MANUAL"]);

// Match-Modus (Optimieren/Bewerten/Ranken). Immutabel ab Erstellung (@adr SPARK-0029-match-three-case-model-optimize-assess-rank@4) — der
// Erstellungs-Picker setzt ihn. Lebt auf der Match.matchMode-Spalte (genesis #159 / V0035),
// nicht mehr im Design-JSONB: dieses Enum ist nur noch der geteilte UI-Typ (MatchData.matchMode).
// Die Token-Liste selbst gehoert ./match-mode — dort haengen die Modus-Verzweigungen der
// Oberflaeche daran, also gibt es sie genau einmal.
export const MatchMode = z.enum(MATCH_MODES);
export type MatchMode = z.infer<typeof MatchMode>;
export type RequirementMode = z.infer<typeof RequirementMode>;

// Muss/Kann fuer die Bewerbungs-Bewertung (genesis CriterionRequirement).
// Nur der Bewerbung-Scorer konsumiert sie; Solver und "Bewerten" ignorieren sie. Auf Read wird
// fehlend/null auf den Default gemappt (OPTIONAL), analog AiModeOnRead und passend zur
// null-Interpretation des genesis-Scorers.
export const CriterionRequirement = z.enum(["MUST", "OPTIONAL"]);
export type CriterionRequirement = z.infer<typeof CriterionRequirement>;
const CriterionRequirementOnRead = z.preprocess((v) => (v == null ? "OPTIONAL" : v), CriterionRequirement);

// Die Regler der Kandidaten-Einschaetzung und der Stufenpruefung: jede Werteliste genau einmal, mit
// ihrem genesis-Default daneben (spark#1068). MatchDesign, RawDesign und MatchData leiten sich hieraus
// ab; die Linsen-Paare (PowerScoreLens, MissingPowerScore) stehen in lib/score-source, weil Grow und
// Save sie mitlesen. Die Reihenfolge ist die der Regler in der Oberflaeche.
export const CandidateAssessmentDetail = z.enum(["BULLETS", "SENTENCES", "LONG_TEXT"]);
export type CandidateAssessmentDetail = z.infer<typeof CandidateAssessmentDetail>;
export const DEFAULT_CANDIDATE_ASSESSMENT_DETAIL: CandidateAssessmentDetail = "SENTENCES";

export const CandidateAssessmentStrictness = z.enum(["NORMAL", "STRICT", "EXTREME"]);
export type CandidateAssessmentStrictness = z.infer<typeof CandidateAssessmentStrictness>;
export const DEFAULT_CANDIDATE_ASSESSMENT_STRICTNESS: CandidateAssessmentStrictness = "NORMAL";

export const CandidateAssessmentOutcomeFocus = z.enum(["RISKS", "BALANCED", "OPPORTUNITIES"]);
export type CandidateAssessmentOutcomeFocus = z.infer<typeof CandidateAssessmentOutcomeFocus>;
export const DEFAULT_CANDIDATE_ASSESSMENT_OUTCOME_FOCUS: CandidateAssessmentOutcomeFocus = "BALANCED";

// Stufenprüfung: wie streng die Mindeststufe von Fachkompetenz-Anforderungen bei FULL_AI geprüft
// wird (LENIENT = Gnadenregel für Muss und Kann, MUST_STRICT = nur Kann kulant). LENIENT ist der
// genesis-Default und damit die Lesart eines Designs ohne den Schluessel.
export const RequirementLeniency = z.enum(["LENIENT", "MUST_STRICT", "STRICT"]);
export type RequirementLeniency = z.infer<typeof RequirementLeniency>;
export const DEFAULT_REQUIREMENT_LENIENCY: RequirementLeniency = "LENIENT";

const Base = {
  operator: Operator,
  composition: Composition,
  active: z.boolean(),
  requirement: CriterionRequirementOnRead,
} as const;

// Permissive on parse – an empty id can briefly exist client-side while a
// row is being added; the codec must not silently drop the row. Producers
// (UI editors) guarantee a real UUID at save time.
const Uuid = z.string();

export const MatchConditionTypename = z.enum([
  "AgeCondition",
  "AnnualSalaryCondition",
  "CertificateCondition",
  "ChildrenCountCondition",
  "CitizenshipCondition",
  "CityCondition",
  "CountryCondition",
  "DriversLicenseCondition",
  "EducationCondition",
  "ExperienceCondition",
  "ExpertiseCondition",
  "GenderCondition",
  "IndustryExperienceCondition",
  "JobDepartmentCondition",
  "JobLocationCondition",
  "LanguageCondition",
  "MaritalCondition",
  "NationalityCondition",
]);
export type MatchConditionTypename = z.infer<typeof MatchConditionTypename>;


/** Integer condition value, rounded on read for the same reason as HealedInt below: a fractional value
 *  failed `.int()`, so the whole condition fell into `unknownConditions` and its row disappeared from
 *  the UI — invisible and unremovable, while the solver still applied it. Rounding recovers those rows;
 *  NumberValueInput rounds so no new ones appear. */
const HealedIntValue = z.preprocess(
  (v) => (typeof v === "number" && Number.isFinite(v) ? Math.round(v) : v),
  z.number().int(),
);
export const MatchCondition = z.discriminatedUnion("__typename", [
  z.object({ __typename: z.literal("AgeCondition"), value: HealedIntValue, ...Base }).passthrough(),
  z.object({ __typename: z.literal("AnnualSalaryCondition"), value: HealedIntValue, ...Base }).passthrough(),
  z.object({ __typename: z.literal("CertificateCondition"), value: z.string(), aiMode: AiModeOnRead, ...Base }).passthrough(),
  z.object({ __typename: z.literal("ChildrenCountCondition"), value: HealedIntValue, ...Base }).passthrough(),
  z.object({ __typename: z.literal("CitizenshipCondition"), id: Uuid, ...Base }).passthrough(),
  z.object({
    __typename: z.literal("CityCondition"),
    value: z.string(),
    aiMode: AiModeOnRead,
    radiusKm: z.number().int().min(1).max(1000).nullable().optional(),
    ...Base,
  }).passthrough(),
  z.object({ __typename: z.literal("CountryCondition"), id: Uuid, ...Base }).passthrough(),
  z.object({ __typename: z.literal("DriversLicenseCondition"), value: z.string(), ...Base }).passthrough(),
  z.object({ __typename: z.literal("EducationCondition"), value: z.string(), aiMode: AiModeOnRead, ...Base }).passthrough(),
  z.object({ __typename: z.literal("ExperienceCondition"), value: z.string(), aiMode: AiModeOnRead, ...Base }).passthrough(),
  z.object({
    __typename: z.literal("ExpertiseCondition"),
    classification: classificationSchema.nullish(),
    expertise: z.string(),
    // Genesis emits level: 0 as "unset" per RequirementsPrompts.java; the
    // strict min(1) was silently dropping such rows on parse. The genesis
    // solver currently ignores level anyway (MatchResultGenerateController
    // doesn't consult condition.level()), so 0 has no functional effect at
    // match time – but the contract should be aligned regardless.
    level: z.number().int().min(0).max(100),
    aiMode: AiModeOnRead,
    ...Base,
  }).passthrough(),
  z.object({ __typename: z.literal("GenderCondition"), id: Uuid, ...Base }).passthrough(),
  z.object({ __typename: z.literal("IndustryExperienceCondition"), industryId: Uuid, minimumYears: z.number(), ...Base }).passthrough(),
  z.object({ __typename: z.literal("JobDepartmentCondition"), id: Uuid, ...Base }).passthrough(),
  z.object({ __typename: z.literal("JobLocationCondition"), id: Uuid, ...Base }).passthrough(),
  z.object({
    __typename: z.literal("LanguageCondition"),
    languageTag: z.string(),
    level: LanguageLevel,
    ...Base,
  }).passthrough(),
  z.object({ __typename: z.literal("MaritalCondition"), id: Uuid, ...Base }).passthrough(),
  z.object({ __typename: z.literal("NationalityCondition"), value: z.string(), ...Base }).passthrough(),
]);
export type MatchCondition = z.infer<typeof MatchCondition>;

export type ConditionByTypename<T extends MatchConditionTypename> = Extract<MatchCondition, { __typename: T }>;

/* ── Company criteria ───────────────────────────────────────────────────────────────────────────
 * A SEPARATE hierarchy, mirroring genesis' own separation: `CompanyMatchCondition` is a sealed
 * interface beside `MatchCondition`, not a subtype of it (the company matching model, "no shared
 * vector space — type-owned match dimensions"). They travel in two different design keys, and a
 * company criterion must never be readable as a person one: folding them into the union above
 * would put a company arm into every person switch (solver input, result blob, AI prompt) that has
 * no such arm to give.
 *
 * What IS shared is the concept frame, so `Base` is reused verbatim: the same cross-cutting members
 * (operator/composition/active), the same Muss/Kann term and type (CriterionRequirement), the same
 * per-candidate evaluation shape. `composition` is carried and inert — a company run never reaches
 * the solver, which is the only thing that evaluates compositions — because the field describes the
 * condition, not the mode. */
export const CompanyMatchConditionTypename = z.enum([
  "CompanyEmployeeCountCondition",
  "CompanyExpertiseCondition",
  "CompanyIndustryCondition",
]);
export type CompanyMatchConditionTypename = z.infer<typeof CompanyMatchConditionTypename>;


export const CompanyMatchCondition = z.discriminatedUnion("__typename", [
  /* "minimumEmployees", never "value": the member name carries the comparison direction, the way
   * IndustryExperienceCondition.minimumYears does. The form is decided and not free — a MINIMUM,
   * not a range and not a sort key (concept v14, reversing 0004 point 1). HealedIntValue for the
   * same reason as every other integer condition: a fractional value would fail `.int()`, and a row
   * that fails to parse disappears from the editor while still being applied. */
  z.object({ __typename: z.literal("CompanyEmployeeCountCondition"), minimumEmployees: HealedIntValue, ...Base }).passthrough(),
  /* "Unternehmen, das X kann": a free-text wording, read against company_competency — a per-company
   * free-text `label` and nothing else. The FORM is mirrored from the person-side ExpertiseCondition
   * rather than derived from it, and since concept v17 it is mirrored only in half:
   *
   * 1. NO `minimumDegree` (v17, `0004` point 3, Kevin with Janek 2026-09-09). Company expertise has
   *    no level on either side — genesis dropped `company_competency.degree` in V0075 — so a
   *    criterion asks WHETHER the company has this expertise, never "at least on level x". The
   *    member and its careful null-vs-0 handling are gone with the thing they described: a stored 0
   *    used to be a real claim ("we cannot do this"), and there is no 0 to claim any more.
   * 2. `aiMode` IS carried, which reverses the 2026-09-02 "hier ist sie die Regel und kein
   *    Schalter". v17 (b) gives the company step ONE switch, in the same "Bewertungsstil" box the
   *    person mode shows, and this is where its state lives — the conditions are the record genesis
   *    reads, exactly as on the person arm. `NO_AI` does not mean "not met": the wording check still
   *    decides and what it does not match reads "nicht bewertbar".
   *
   * READ-DEFAULTED TO FULL_AI, opposite to the person arm's NO_AI default. A company draft written
   * before v17 carries no aiMode at all, and before v17 the AI was on without exception — so
   * assuming off would silently hand an old draft a setting its author could not have chosen.
   * genesis defaults the same way, in the record's own constructor. */
  z.object({
    __typename: z.literal("CompanyExpertiseCondition"),
    expertise: z.string(),
    aiMode: CompanyAiModeOnRead,
    ...Base,
  }).passthrough(),
  /* A plain reference comparison against company.industry_id (genesis V0044), NOT the person-side
   * IndustryExperienceCondition: a company HAS one industry on the row, so there is no duration to
   * be gradual about. Hence an id and no minimumYears. */
  z.object({ __typename: z.literal("CompanyIndustryCondition"), industryId: Uuid, ...Base }).passthrough(),
]);
export type CompanyMatchCondition = z.infer<typeof CompanyMatchCondition>;

export type CompanyConditionByTypename<T extends CompanyMatchConditionTypename> = Extract<
  CompanyMatchCondition,
  { __typename: T }
>;

// Matches genesis `TeamProfile { id, name, elementWeights: Map<CreativityElement, Integer> }`.
// Keys of `elementWeights` are full enum strings (e.g. "PERSEVERANCE"), not
// the 3-letter UI abbreviations; the UI layer translates for display.
export const TeamProfile = z
  .object({
    id: Uuid,
    name: z.string(),
    "elementWeights": z.record(z.string(), z.number().int()),
  })
  .passthrough();
export type TeamProfile = z.infer<typeof TeamProfile>;

export const RecruitingTeamFitBasis = z.object({
  members: z.array(z.object({ id: Uuid, name: z.string() })).min(1),
  scorecard: TeamProfile,
});
export type RecruitingTeamFitBasis = z.infer<typeof RecruitingTeamFitBasis>;

export const RecruitingTeamFitSetup = z.object({
  enabled: z.boolean(),
  members: z.array(z.object({ id: Uuid, name: z.string() })),
  scorecard: TeamProfile.nullable(),
});
export type RecruitingTeamFitSetup = z.infer<typeof RecruitingTeamFitSetup>;

// Wire types. Cross-platform canonical term is "Project" (genesis MR !236 /
// @adr SPARK-0021-project-umbrella-with-projectdocument-cross-repo@1) — the wire/JSON key is now `project` end-to-end.
// Matches genesis `ProjectDocument { backdropFileId, name, size?, mimeType? }`.
// ProjectDocument lifecycle is managed via the attach/detach mutations (genesis
// MR !236), but UpdateMatch still round-trips `documents` because genesis writes
// the JSONB design wholesale — omitting the field would delete every attached
// document on the next text edit (see match-design-mapping.ts buildProjectWire).
export const ProjectDocument = z
  .object({
    "backdropFileId": z.string(),
    name: z.string(),
    size: z.number().int().nullable().optional(),
    "mimeType": z.string().nullable().optional(),
  })
  .passthrough();
export type ProjectDocument = z.infer<typeof ProjectDocument>;

// Matches genesis `Project { name, shortDescription, description, documents }`.
// All fields nullable; the snapshot can be partially populated (e.g. user typed
// a name but no description yet).
export const Project = z
  .object({
    name: z.string().nullable().optional(),
    "shortDescription": z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    documents: z.array(ProjectDocument).nullable().optional(),
  })
  .passthrough();
export type Project = z.infer<typeof Project>;

export interface MatchDesign {
  teamCount: number | null;
  teamSize: number | null;
  teamProfile: TeamProfile | null;
  recruitingTeamFit?: RecruitingTeamFitBasis | null;
  recruitingTeamFitSetup?: RecruitingTeamFitSetup | null;
  conditions: MatchCondition[];
  // Conditions spark's typed schema can't parse (a genesis variant added on
  // the server before spark models it, or a malformed value from another
  // client) are kept here verbatim and re-emitted by serializeDesign, never
  // dropped. The editors only ever read/write typed `conditions`, so an
  // unknown row is ignored for display/editing but never lost from storage.
  // Optional on the type only so existing MatchDesign literals stay valid;
  // parseDesign always populates it (empty when everything parsed).
  unknownConditions?: unknown[];
  /**
   * The company criteria of a company run (RANK_COMPANIES), BESIDE — never instead of — the person
   * criteria in `conditions`.
   *
   * `null` means "this design has no company arm" and is the normal case: a person design never
   * wrote the key. Deliberately NOT normalised to `[]`, unlike `conditions` — genesis draws the
   * same distinction on its own record and pins it in a test ("a person design leaves the company
   * arm unset, not empty"): "no company arm in this design" and "a company arm with no criteria"
   * are two different statements, and only the second one is a user input. The editor materialises
   * the key on its first write and keeps `[]` after the last row is deleted.
   */
  companyConditions: CompanyMatchCondition[] | null;
  /**
   * Company criteria spark's typed schema can't parse, kept verbatim and re-emitted — the company
   * twin of `unknownConditions`. All THREE kinds genesis' sealed hierarchy holds today are typed
   * above, expertise included, so nothing a current genesis writes lands here: what this catches is
   * a FOURTH kind added later, or a malformed row from a peer client. Dropping either would delete a
   * criterion the user authored elsewhere; the editor shows it as a count and never as a row.
   */
  unknownCompanyConditions?: unknown[];
  aiCopilotText: string | null;
  candidateAssessmentPrompt?: string | null;
  candidateAssessmentDetail?: CandidateAssessmentDetail | null;
  candidateAssessmentStrictness?: CandidateAssessmentStrictness | null;
  candidateAssessmentOutcomeFocus?: CandidateAssessmentOutcomeFocus | null;
  // Stufenprüfung: wie streng die Mindeststufe von Fachkompetenz-Anforderungen bei FULL_AI
  // geprüft wird. null = DEFAULT_REQUIREMENT_LENIENCY (Gnadenregel, genesis-Default).
  requirementLeniency?: RequirementLeniency | null;
  // Aus welchem Instrument die Power-Werte eines Personen-Laufs kommen, und was eine Power
  // zaehlt, fuer die es keinen Wert gibt (genesis#328,
  // @external-adr GENESIS-0044-a-match-chooses-which-instrument-its-power-scores-come-from,
  // @external-adr GENESIS-0045-an-unmeasured-power-counts-as-zero-unless-the-match-says-neutral).
  // Genesis liest beide in OPTIMIZE, ASSESS und RANK; der Unternehmens-Pfad hat keine Powers.
  // null = der Default des Backends, also das Verhalten vor den Schluesseln:
  // DEFAULT_POWER_SCORE_LENS bzw. DEFAULT_MISSING_POWER_SCORE.
  powerScoreLens?: PowerScoreLens | null;
  missingPowerScore?: MissingPowerScore | null;
  requirementMode: RequirementMode | null;
  agenticAiSupported: boolean | null;
  /**
   * The AI agents this project is equipped with — project CONTEXT, never team members (@adr SPARK-0034-ai-agents-are-project-context-not-team-members@2).
   * The optimiser never sees them; they shape the requirement discussion and are shown with the
   * result. Stores a snapshot rather than bare ids so the entry stays legible to genesis and to the
   * assistant without the agent list, and survives the row's deletion; display detail is resolved
   * from the genesis `agents` list by `id` (see `agents.ts`).
   */
  agents: MatchAgent[];
  project: Project | null;
}

/** What a match stores per selected agent: a snapshot, not a pointer. Small and self-describing. */
export interface MatchAgent {
  id: string;
  name: string;
  role: string;
  covers: string[];
}

export const MATCH_DESIGN_VERSION = 2;

/**
 * What `parseDesign` guarantees beyond {@link MatchDesign}.
 *
 * The two preserved-unknown lists are OPTIONAL on `MatchDesign`, because a design object built
 * anywhere else need not carry them — but every exit of the parse sets both, so a caller that got
 * its design from here has no absent case to default. Saying so in the type removes a `?? []` at
 * each call site that no input could reach, and would have hidden a real regression if the parse
 * ever stopped setting them.
 */
export type ParsedDesign = MatchDesign & {
  unknownConditions: unknown[];
  unknownCompanyConditions: unknown[];
};

export const EMPTY_DESIGN: ParsedDesign = {
  recruitingTeamFit: null,
  recruitingTeamFitSetup: null,
  teamCount: null,
  teamSize: null,
  teamProfile: null,
  conditions: [],
  unknownConditions: [],
  companyConditions: null,
  unknownCompanyConditions: [],
  aiCopilotText: null,
  candidateAssessmentPrompt: null,
  candidateAssessmentDetail: null,
  candidateAssessmentStrictness: null,
  candidateAssessmentOutcomeFocus: null,
  requirementLeniency: null,
  powerScoreLens: null,
  missingPowerScore: null,
  requirementMode: null,
  agenticAiSupported: null,
  agents: [],
  project: null,
};

// A count that a peer client (or spark, before the producer was bounded) may have written as a
// non-integer. `parseDesign` uses the THROWING RawDesign.parse and runs on every render of the match
// editor, so a fractional teamSize made that match permanently unopenable — the value could not even be
// corrected, because the editor would not render. Rounding on READ heals those matches; the producer is
// bounded separately so new ones cannot be created.
const HealedInt = z.preprocess(
  (v) => (typeof v === "number" && Number.isFinite(v) ? Math.round(v) : v),
  z.number().int().nullable().optional(),
);

// Tolerant on read: a malformed entry is dropped rather than making the match unopenable, the same
// stance parseDesign takes for conditions. Demo data and hand-edited blobs both land here.
const MatchAgentRefSchema = z.object({
  id: z.string(),
  name: z.string(),
  role: z.string().optional().default(""),
  covers: z.array(z.string()).optional().default([]),
});

const RawDesign = z.object({
  "teamCount": HealedInt,
  "teamSize": HealedInt,
  "teamProfile": TeamProfile.nullable().optional(),
  recruitingTeamFit: RecruitingTeamFitBasis.nullish().catch(null),
  recruitingTeamFitSetup: RecruitingTeamFitSetup.nullish().catch(null),
  "conditions": z.array(z.unknown()).optional(),
  // Read untyped, split per row below: `nullable` so the absent key and an explicit null both mean
  // "no company arm", and an array of unknown so ONE unparseable row cannot cost the whole arm.
  "companyConditions": z.array(z.unknown()).nullable().optional(),
  "aiCopilotText": z.string().nullable().optional(),
  "candidateAssessmentPrompt": z.string().nullable().optional(),
  "candidateAssessmentDetail": CandidateAssessmentDetail.nullable().optional(),
  "candidateAssessmentStrictness": CandidateAssessmentStrictness.nullable().optional(),
  "candidateAssessmentOutcomeFocus": CandidateAssessmentOutcomeFocus.nullable().optional(),
  "requirementLeniency": RequirementLeniency.nullable().optional(),
  "powerScoreLens": PowerScoreLens.nullable().optional(),
  "missingPowerScore": MissingPowerScore.nullable().optional(),
  "requirementMode": RequirementMode.nullable().optional(),
  "agenticAiSupported": z.boolean().nullable().optional(),
  "agents": z.array(MatchAgentRefSchema).nullable().optional(),
  "project": Project.nullable().optional(),
}).passthrough();

export function parseDesign(input: unknown): ParsedDesign {
  if (input == null || typeof input !== "object" || Array.isArray(input)) {
    return { ...EMPTY_DESIGN };
  }
  const raw = RawDesign.parse(input);

  // Preserve, don't drop. A condition spark can't type is round-tripped
  // verbatim by serializeDesign instead of being deleted on the next
  // autosave. See the `unknownConditions` field docs + the 2026-07-17
  // incident where a peer client wrote composition.value as the string
  // "null" and every Expertise/Industry criterion vanished on save.
  const conditions: MatchCondition[] = [];
  const unknownConditions: unknown[] = [];
  for (const c of raw.conditions ?? []) {
    const parsed = MatchCondition.safeParse(c);
    if (parsed.success) {
      conditions.push(parsed.data);
      continue;
    }
    /* v8 ignore next -- unreachable in any environment spark runs in (browser, jsdom): the guard exists
       only so a console-less host cannot turn a preserved condition into a crash */
    if (typeof console !== "undefined" && typeof console.warn === "function") {
      console.warn(
        "[design-codec] preserving unrecognised MatchCondition (round-tripped, not editable)",
        c,
        parsed.error.issues,
      );
    }
    unknownConditions.push(c);
  }

  // Same split for the company arm, with one difference that carries meaning: an ABSENT key stays
  // null. Only a present key produces a list, so serializeDesign can put the design back the way it
  // found it instead of turning "no company arm" into "an empty company arm".
  const rawCompany = raw["companyConditions"];
  let companyConditions: CompanyMatchCondition[] | null = null;
  const unknownCompanyConditions: unknown[] = [];
  if (rawCompany != null) {
    companyConditions = [];
    for (const c of rawCompany) {
      const parsed = CompanyMatchCondition.safeParse(c);
      if (parsed.success) {
        companyConditions.push(parsed.data);
        continue;
      }
      /* v8 ignore next -- unreachable in any environment spark runs in (browser, jsdom); see the
         person branch above for why the guard exists at all */
      if (typeof console !== "undefined" && typeof console.warn === "function") {
        console.warn(
          "[design-codec] preserving unrecognised CompanyMatchCondition (round-tripped, not editable)",
          c,
          parsed.error.issues,
        );
      }
      unknownCompanyConditions.push(c);
    }
  }

  return {
    teamCount: raw["teamCount"] ?? null,
    teamSize: raw["teamSize"] ?? null,
    teamProfile: raw["teamProfile"] ?? null,
    recruitingTeamFit: raw.recruitingTeamFit ?? null,
    recruitingTeamFitSetup: raw.recruitingTeamFitSetup ?? null,
    conditions,
    unknownConditions,
    companyConditions,
    unknownCompanyConditions,
    aiCopilotText: raw["aiCopilotText"] ?? null,
    candidateAssessmentPrompt: raw["candidateAssessmentPrompt"] ?? null,
    candidateAssessmentDetail: raw["candidateAssessmentDetail"] ?? null,
    candidateAssessmentStrictness: raw["candidateAssessmentStrictness"] ?? null,
    candidateAssessmentOutcomeFocus: raw["candidateAssessmentOutcomeFocus"] ?? null,
    requirementLeniency: raw["requirementLeniency"] ?? null,
    powerScoreLens: raw["powerScoreLens"] ?? null,
    missingPowerScore: raw["missingPowerScore"] ?? null,
    requirementMode: raw["requirementMode"] ?? null,
    agenticAiSupported: raw["agenticAiSupported"] ?? null,
    agents: raw["agents"] ?? [],
    project: raw["project"] ?? null,
  };
}

// Emits only genesis-known design keys. Spark-private UI state must either get
// a typed home in MatchDesign or stay React-only; otherwise another client
// cannot preserve or understand it even though genesis tolerates unknown
// legacy keys on read.
export function serializeDesign(design: MatchDesign): Record<string, unknown> {
  const out: Record<string, unknown> = { version: MATCH_DESIGN_VERSION };
  if (design.teamCount != null) out["teamCount"] = design.teamCount;
  if (design.teamSize != null) out["teamSize"] = design.teamSize;
  if (design.teamProfile != null) out["teamProfile"] = design.teamProfile;
  if (design.recruitingTeamFitSetup != null) out.recruitingTeamFitSetup = design.recruitingTeamFitSetup;
  if (design.recruitingTeamFit != null) out.recruitingTeamFit = design.recruitingTeamFit;
  // Re-append conditions spark couldn't type so they survive the round-trip
  // (see parseDesign / the MatchDesign.unknownConditions docs). They stay
  // inside the known `conditions` key, so genesis's strict top-level
  // deserialization is unaffected.
  const unknownConditions = design.unknownConditions ?? [];
  out["conditions"] =
    unknownConditions.length > 0 ? [...design.conditions, ...unknownConditions] : design.conditions;
  // The company arm is emitted only once it exists: null means the design never had one, and
  // writing `[]` there would silently turn that into the user statement "a company arm with no
  // criteria" on the next autosave of any person match. The unknown-kind check is not redundant —
  // it is the belt to the braces: a preserved row must never be lost to a null typed list.
  const unknownCompanyConditions = design.unknownCompanyConditions ?? [];
  if (design.companyConditions != null || unknownCompanyConditions.length > 0) {
    out["companyConditions"] = [...(design.companyConditions ?? []), ...unknownCompanyConditions];
  }
  if (design.aiCopilotText != null) out["aiCopilotText"] = design.aiCopilotText;
  if (design.candidateAssessmentPrompt != null) {
    out["candidateAssessmentPrompt"] = design.candidateAssessmentPrompt;
  }
  if (design.candidateAssessmentDetail != null) {
    out["candidateAssessmentDetail"] = design.candidateAssessmentDetail;
  }
  if (design.candidateAssessmentStrictness != null) {
    out["candidateAssessmentStrictness"] = design.candidateAssessmentStrictness;
  }
  if (design.candidateAssessmentOutcomeFocus != null) {
    out["candidateAssessmentOutcomeFocus"] = design.candidateAssessmentOutcomeFocus;
  }
  if (design.requirementLeniency != null) {
    out["requirementLeniency"] = design.requirementLeniency;
  }
  if (design.powerScoreLens != null) out["powerScoreLens"] = design.powerScoreLens;
  if (design.missingPowerScore != null) out["missingPowerScore"] = design.missingPowerScore;
  if (design.requirementMode != null) out["requirementMode"] = design.requirementMode;
  if (design.agenticAiSupported != null) out["agenticAiSupported"] = design.agenticAiSupported;
  // Always emitted once non-empty; genesis does not model the key but preserves it (genesis#264),
  // and Match.design resolves from the raw map, so the assistant reads it too.
  if (design.agents.length > 0) out["agents"] = design.agents;
  // matchMode is NOT emitted: @adr SPARK-0029-match-three-case-model-optimize-assess-rank@4 §2 moved it to the immutable Match.matchMode
  // column (genesis #159 / V0035). genesis strips it from the blob anyway
  // (withoutMatchMode on create + update), so persisting it here would only re-introduce
  // the drift-prone dual representation the ADR removed.
  if (design.project != null) out["project"] = design.project;
  return out;
}

/**
 * A fresh company criterion. Separate factory rather than a generic over both unions: the defaults
 * differ where they must. `operator` defaults to GE, because two of the three company criteria are
 * thresholds (a minimum employee count, a minimum expertise degree) and GE is the honest default
 * for those; the industry comparison is an id and passes EQ explicitly. `composition` is the
 * neutral ALL/null, which is what genesis' own round-trip tests write, since nothing evaluates it
 * on this arm.
 */
export function newCompanyCondition<T extends CompanyMatchConditionTypename>(
  typename: T,
  partial: Omit<CompanyConditionByTypename<T>, "__typename" | keyof typeof Base | "active"> & {
    operator?: Operator;
    composition?: Composition;
    active?: boolean;
  },
): CompanyConditionByTypename<T> {
  return {
    __typename: typename,
    operator: partial.operator ?? "GE",
    composition: partial.composition ?? { operator: "ALL", value: null },
    active: partial.active ?? true,
    requirement: "OPTIONAL",
    // AI ON for a fresh expertise criterion, which is what the toggle shows by default and what the
    // codec reads a missing aiMode as. Set HERE so an authored row already equals what a round trip
    // through genesis gives back — otherwise the editor holds a row without the member, the reload
    // holds one with it, and the rebase in match-design-mapping sees a change nobody made.
    ...(typename === "CompanyExpertiseCondition" ? { aiMode: "FULL_AI" as const } : {}),
    ...partial,
  } as CompanyConditionByTypename<T>;
}

export function newCondition<T extends MatchConditionTypename>(
  typename: T,
  partial: Omit<ConditionByTypename<T>, "__typename" | keyof typeof Base | "active"> & {
    operator?: Operator;
    composition?: Composition;
    active?: boolean;
    requirement?: CriterionRequirement;
  },
): ConditionByTypename<T> {
  return {
    ...partial,
    __typename: typename,
    // Diese vier Felder MUESSEN nach dem Spread stehen: partial's Object-Spread kopiert
    // eine Property, sobald ihr Key existiert, unabhaengig davon ob ihr Wert undefined
    // (oder null) ist - anders als `??`, das genau dafuer den Fallback nehmen wuerde.
    // Vor 2026-09-01 stand `...partial` als letztes und ein explizit uebergebenes
    // `composition: undefined` (ExpertiseSection, Rank-Fall) hat den bereits berechneten
    // Default wieder verworfen.
    operator: partial.operator ?? "EQ",
    composition: partial.composition ?? { operator: "ALL", value: null },
    active: partial.active ?? true,
    requirement: partial.requirement ?? "OPTIONAL",
  } as ConditionByTypename<T>;
}
